import React, { useState } from "react";

export default function CardFlipGame() {
  const [score, setScore] = useState(0);
  const [currentCard, setCurrentCard] = useState("");
  const [gameStatus, setGameStatus] = useState("playing"); // 'playing', 'failed', 'ended'
  const [isFlipping, setIsFlipping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordList, setRecordList] = useState([]);

  const cards = ["A", 2, 3, 4, 5, 6, 7, 8, 9, 10, "J", "Q", "K"];

  const getCardValue = (card) => {
    if (card === "A") return 1;
    if (card === "J") return 11;
    if (card === "Q") return 12;
    if (card === "K") return 13;
    return card;
  };

  const getCardColor = (card) => {
    const redCards = ["A", "J", "Q", "K"];
    return redCards.includes(card) ? "text-red-600" : "text-black";
  };

  const flipCard = () => {
    if (gameStatus !== "playing") return;

    setIsFlipping(true);

    // 添加翻牌动画效果
    setTimeout(() => {
      const card = cards[Math.floor(Math.random() * cards.length)];
      const value = getCardValue(card);
      const newScore = score + (value % 3);

      setCurrentCard(card);
      setScore(newScore);

      // 如果开启了记录功能，则记录本次翻牌
      if (isRecording) {
        // 检查是否有初始记录，如果有则从第1次开始计算，否则从当前记录数+1开始
        const hasInitialRecord =
          recordList.length > 0 && recordList[0].card === "初始";
        const currentRound = hasInitialRecord
          ? recordList.length
          : recordList.length + 1;

        setRecordList((prev) => [
          ...prev,
          {
            card: card,
            score: newScore,
            round: currentRound,
          },
        ]);
      }

      if (newScore > 10) {
        setScore(0);
        setGameStatus("failed");
      }

      setIsFlipping(false);
    }, 300);
  };

  const endGame = () => {
    if (gameStatus === "playing") {
      setGameStatus("ended");
    }
  };

  const restartGame = () => {
    setScore(0);
    setCurrentCard("");
    setGameStatus("playing");
    setIsFlipping(false);
    setRecordList([]);
  };

  const toggleRecording = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      // 开始记录时清空之前的记录
      setRecordList([]);
      // 如果当前已有分数，先记录当前状态
      if (score > 0) {
        setRecordList([
          {
            card: "初始",
            score: score,
            round: 0,
          },
        ]);
      }
    }
  };

  const getScoreDisplay = () => {
    if (gameStatus === "failed") {
      return "游戏失败，最终得分：0分";
    }
    if (gameStatus === "ended") {
      return `最终得分：${score}分`;
    }
    return `总分：${score}分`;
  };

  const getScoreColor = () => {
    if (gameStatus === "failed") return "text-red-600";
    if (gameStatus === "ended") return "text-blue-600";
    return "text-gray-800";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
          翻牌游戏
        </h1>

        {/* 卡牌显示区域 */}
        <div className="flex justify-center mb-8">
          <div
            className={`w-32 h-44 border-2 border-gray-800 rounded-lg flex items-center justify-center text-6xl font-bold bg-white shadow-md transition-all duration-300 ${
              isFlipping ? "transform rotate-y-180 scale-95" : ""
            } ${currentCard ? getCardColor(currentCard) : "text-gray-400"}`}
            style={{
              transformStyle: "preserve-3d",
            }}
          >
            {isFlipping ? "?" : currentCard || "?"}
          </div>
        </div>

        {/* 分数显示 */}
        <div
          className={`text-2xl font-semibold text-center mb-8 ${getScoreColor()}`}
        >
          {getScoreDisplay()}
        </div>

        {/* 按钮区域 */}
        <div className="space-y-3">
          <button
            onClick={flipCard}
            disabled={gameStatus !== "playing" || isFlipping}
            className="w-full py-3 px-6 bg-green-500 hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200 transform hover:scale-105"
          >
            {isFlipping ? "翻牌中..." : "翻牌"}
          </button>

          <div className="flex gap-3">
            <button
              onClick={endGame}
              disabled={gameStatus !== "playing"}
              className="flex-1 py-3 px-6 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors duration-200"
            >
              结束游戏
            </button>

            <button
              onClick={restartGame}
              className="flex-1 py-3 px-6 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              重新开始
            </button>
          </div>

          <button
            onClick={toggleRecording}
            className={`w-full py-3 px-6 font-semibold rounded-lg transition-colors duration-200 ${
              isRecording
                ? "bg-red-500 hover:bg-red-600 text-white"
                : "bg-purple-500 hover:bg-purple-600 text-white"
            }`}
          >
            {isRecording ? "停止记录" : "开始记录"}
          </button>
        </div>

        {/* 游戏状态提示 */}
        {gameStatus === "failed" && (
          <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-center">
            游戏失败了！再试一次吧！
          </div>
        )}

        {gameStatus === "ended" && score <= 10 && (
          <div className="mt-4 p-3 bg-green-100 border border-green-300 rounded-lg text-green-700 text-center">
            游戏结束，你最终得分为：{score}分
          </div>
        )}

        {/* 记录显示区域 */}
        {isRecording && recordList.length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center">
              翻牌记录
            </h3>
            <div className="max-h-40 overflow-y-auto">
              <div className="space-y-2">
                {recordList.map((record, index) => (
                  <div
                    key={index}
                    className="flex justify-between items-center p-2 bg-white rounded border"
                  >
                    <span className="text-sm text-gray-600">
                      第{record.round}次
                    </span>
                    <span className="font-medium">翻到：{record.card}</span>
                    <span className="text-blue-600 font-semibold">
                      得分：{record.score}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
